import { useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ZONE_TYPES } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

const zoneTypeLabels: Record<string, string> = {
  field: "Field",
  spectator: "Spectator Area",
  parking: "Parking",
  facility: "Facility",
};

export default function ZoneCreatePage() {
  const navigate = useNavigate();
  const { schoolId } = useParams<{ schoolId: string }>();
  const [name, setName] = useState("");
  const [zoneType, setZoneType] = useState<string>("field");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!schoolId) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.createZone(schoolId, {
        name,
        zone_type: zoneType as typeof ZONE_TYPES[number],
        latitude: latitude ? parseFloat(latitude) : undefined,
        longitude: longitude ? parseFloat(longitude) : undefined,
      });
      navigate("/zones");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create zone");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="zone-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Zone</h1>
        <p className="text-muted-foreground">Create a new zone for this school campus.</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Zone Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="zone-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder="e.g. Main Field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="zone-type">Zone Type</Label>
              <select
                id="zone-type"
                data-field="zone-type"
                value={zoneType}
                onChange={(e) => setZoneType(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {ZONE_TYPES.map((zt) => (
                  <option key={zt} value={zt}>
                    {zoneTypeLabels[zt] ?? zt}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="latitude">Latitude (optional)</Label>
              <Input
                id="latitude"
                data-field="latitude"
                type="number"
                step="any"
                placeholder="e.g. -33.9248685"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="longitude">Longitude (optional)</Label>
              <Input
                id="longitude"
                data-field="longitude"
                type="number"
                step="any"
                placeholder="e.g. 18.4240553"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create Zone"}
              </Button>
              <Link to="/zones" className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
